import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import {
  getFindByRequests,
  getVisitingBookings,
  getAvailableDistricts,
} from '../services/dataGridService';

/**
 * Data Grid Page
 *
 * Displays detailed lists for:
 * - Find by request submissions
 * - Visiting bookings (new/pending/done)
 *
 * Features:
 * - Filter bar with district, date range, and status filters
 * - Refresh button reloads both lists with current filters applied
 * - Auto-refresh data on page navigation
 * - Detail modal for find by request rows
 */
const DataGridPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [findByRequests, setFindByRequests] = useState([]);
  const [visitingBookings, setVisitingBookings] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'findByRequest');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [districtFilter, setDistrictFilter] = useState('all');
  const [districts, setDistricts] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Auto-refresh data on page access — increments refreshKey on mount
  useEffect(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Load available districts
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const availableDistricts = await getAvailableDistricts();
        setDistricts(availableDistricts);
      } catch (err) {
        console.error('[DataGrid] Error loading districts:', err);
      }
    };
    loadDistricts();
  }, []);

  // Load data with filters — refreshKey triggers reload on page access or manual refresh
  useEffect(() => {
    const loadData = async () => {
      if (refreshKey === 0) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      try {
        const filterOptions = {
          district: districtFilter,
          startDate: dateFrom || null,
          endDate: dateTo || null,
        };

        const [findBy, visiting] = await Promise.all([
          getFindByRequests(filterOptions),
          getVisitingBookings({ ...filterOptions, status: statusFilter }),
        ]);
        setFindByRequests(findBy);
        setVisitingBookings(visiting);
      } catch (err) {
        console.error('[DataGrid] Error loading data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setInitialLoading(false);
        setRefreshing(false);
      }
    };

    loadData();
  }, [statusFilter, districtFilter, dateFrom, dateTo, refreshKey]);

  // Filter visiting bookings by status for display
  const filteredVisitingBookings = useMemo(() => {
    if (activeTab !== 'visiting') return [];
    return visitingBookings;
  }, [activeTab, visitingBookings]);

  // Tabs configuration
  const tabs = [
    { id: 'findByRequest', label: t('dashboard.findByRequest.title'), icon: '🔍' },
    { id: 'visiting', label: t('dashboard.visitingBookings.title'), icon: '📅' },
  ];

  // Status filters for visiting bookings
  const statusFilters = [
    { value: 'all', label: t('dashboard.period.all') },
    { value: 'new', label: t('dashboard.visitingBookings.new') },
    { value: 'pending', label: t('dashboard.visitingBookings.pending') },
    { value: 'done', label: t('dashboard.visitingBookings.done') },
  ];

  // Format date with time
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  // Format date only (no time)
  const formatDateOnly = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleResetFilters = () => {
    setDistrictFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedRequest(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // --- Initial loading skeleton (only on very first load) ---
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-96 mb-8" />
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Error state (only shown when there's truly no data) ---
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 animate-scale-in">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('dashboard.error')}</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('dashboard.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <AdminBreadcrumb />

        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.dataGrid.title')}</h1>
              <p className="text-gray-600 mt-2">
                {activeTab === 'findByRequest'
                  ? `${t('dashboard.findByRequest.total')}: ${findByRequests.length}`
                  : `${t('dashboard.visitingBookings.title')} (${visitingBookings.length} ${t('dashboard.visitingBookings.total')})`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                title={t('dashboard.refresh')}
              >
                <svg
                  className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                ← {t('dashboard.title')}
              </button>
            </div>
          </div>

          {/* Refreshing inline indicator */}
          {refreshing && (
            <div className="mt-3 flex items-center gap-2 text-sm text-primary-600 animate-fade-in-up">
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Đang tải lại dữ liệu...</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg mb-6 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('about.form.district')}
              </label>
              <select
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">{t('dashboard.period.all')}</option>
                {districts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Từ ngày
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Đến ngày
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
              >
                {t('filter.reset')}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`?tab=${tab.id}`);
                }}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Status Filters (only for visiting bookings) */}
          {activeTab === 'visiting' && (
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-2">
                {statusFilters.map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setStatusFilter(filter.value)}
                    className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                      statusFilter === filter.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Data Grid */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {activeTab === 'findByRequest' && (
            <FindByRequestGrid
              data={findByRequests}
              formatDate={formatDate}
              formatDateOnly={formatDateOnly}
              onRowClick={setSelectedRequest}
            />
          )}

          {activeTab === 'visiting' && (
            <VisitingBookingsGrid
              data={filteredVisitingBookings}
              formatDate={formatDate}
            />
          )}
        </div>
      </div>

      {/* Find By Request Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          formatDate={formatDate}
          formatDateOnly={formatDateOnly}
          onClose={() => setSelectedRequest(null)}
        />
      )}
    </div>
  );
};

/**
 * Find by Request Detail Modal
 */
const RequestDetailModal = ({ request, formatDate, formatDateOnly, onClose }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation on mount
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  // Parse amenities from comma-separated string or array
  const amenities = useMemo(() => {
    if (!request.amenities || request.amenities === 'N/A') return [];
    if (Array.isArray(request.amenities)) return request.amenities;
    return request.amenities.split(',').map((a) => a.trim()).filter(Boolean);
  }, [request.amenities]);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200 ${
        isVisible ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto transition-all duration-300 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{t('dashboard.findByRequest.title')}</h3>
              <p className="text-xs text-gray-500">{formatDate(request.timestamp)}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Name + Phone row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('about.form.name')}</p>
              <p className="text-base font-semibold text-gray-900">{request.name}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('about.form.phone')}</p>
              <a
                href={`tel:${request.phone}`}
                className="text-base font-semibold text-primary-600 hover:text-primary-700 hover:underline"
              >
                {request.phone}
              </a>
            </div>
          </div>

          {/* Property Type + District row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('admin.propertyType')}</p>
              <p className="text-base font-medium text-gray-900">{request.propertyType}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('about.form.district')}</p>
              <p className="text-base font-medium text-gray-900">{request.district}</p>
            </div>
          </div>

          {/* Move-in Date */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('about.form.moveInDate')}</p>
            <p className="text-base font-medium text-gray-900">
              {formatDateOnly(request.moveInDate)}
            </p>
          </div>

          {/* Amenities */}
          {amenities.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{t('about.form.amenities')}</p>
              <div className="flex flex-wrap gap-2">
                {amenities.map((amenity, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-medium"
                  >
                    {amenity}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Note */}
          {request.note && request.note !== 'N/A' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <div>
                  <p className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-1">{t('about.form.note')}</p>
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{request.note}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
          <a
            href={`tel:${request.phone}`}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {t('detail.phone')}
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Find by Request Grid Component
 */
const FindByRequestGrid = ({ data, formatDate, formatDateOnly, onRowClick }) => {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-lg">{t('dashboard.noData')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('about.form.name')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('about.form.phone')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.propertyType')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('about.form.district')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('about.form.moveInDate')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('dashboard.lastUpdated')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr
              key={item.id || index}
              className="hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => onRowClick(item)}
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{item.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.phone}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.propertyType}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.district}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDateOnly(item.moveInDate)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(item.timestamp)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClick(item);
                  }}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Xem chi tiết"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Visiting Bookings Grid Component
 */
const VisitingBookingsGrid = ({ data, formatDate }) => {
  const { t } = useTranslation();

  const getStatusBadge = (event) => {
    if (event.isFinalized) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">{t('dashboard.visitingBookings.done')}</span>;
    }
    if (event.isRevisit) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">{t('dashboard.visitingBookings.pending')}</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{t('dashboard.visitingBookings.new')}</span>;
  };

  if (data.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p className="text-lg">{t('dashboard.noData')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('calendar.userName')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('calendar.phone')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('calendar.district')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('calendar.propertyName')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('calendar.dateTime')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('dashboard.visitingBookings.title')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('dashboard.lastUpdated')}</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((event, index) => (
            <tr key={event.id || index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{event.userName}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{event.phone}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{event.district}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{event.propertyName}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(event.date)}</td>
              <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(event)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(event.createdAt || event.date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DataGridPage;